import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { CheckCircle, Cancel, ArrowBack } from '@mui/icons-material';
import axios from 'axios';

interface ConsentTemplate {
  id: string;
  title: string;
  description: string;
  consent_type: string;
  is_required: boolean;
  version: string;
  content_html?: string;
}

interface UserConsent {
  id: string;
  template_id: string;
  consent_type: string;
  status: string;
  granted_at: string | null;
  template?: ConsentTemplate;
}

function ConsentManagementPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ConsentTemplate[]>([]);
  const [userConsents, setUserConsents] = useState<UserConsent[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ConsentTemplate | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadTemplates(), loadUserConsents()]);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    const response = await axios.get(`${API_URL}/api/v1/settings/consent/templates`);
    setTemplates(response.data);
  };

  const loadUserConsents = async () => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.get(`${API_URL}/api/v1/settings/consent/my`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUserConsents(response.data);
  };

  const handleGrantConsent = async (templateId: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API_URL}/api/v1/settings/consent/grant?template_id=${templateId}&acceptance_method=admin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadUserConsents();
      setDialogOpen(false);
      setError(null);
    } catch (err: any) {
      setError('ไม่สามารถบันทึกความยินยอมได้');
      console.error('Failed to grant consent:', err);
    }
  };

  const handleWithdrawConsent = async (consentId: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API_URL}/api/v1/settings/consent/${consentId}/withdraw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadUserConsents();
      setError(null);
    } catch (err: any) {
      setError('ไม่สามารถถอนความยินยอมได้');
      console.error('Failed to withdraw consent:', err);
    }
  };

  const hasConsent = (templateId: string) => {
    return userConsents.some(
      (c) => c.template_id === templateId && c.status === 'granted'
    );
  };

  const getConsentId = (templateId: string) => {
    const consent = userConsents.find(
      (c) => c.template_id === templateId && c.status === 'granted'
    );
    return consent?.id;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/profile')}
        sx={{ mb: 2 }}
      >
        กลับ
      </Button>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
        จัดการความยินยอม
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        คุณสามารถให้หรือถอนความยินยอมในการใช้ข้อมูลส่วนบุคคลได้ตลอดเวลา
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {templates.length === 0 ? (
          <Alert severity="info">ไม่มีข้อมูลความยินยอม</Alert>
        ) : (
          <List>
            {templates.map((template, index) => {
              const hasUserConsent = hasConsent(template.id);
              const consentId = getConsentId(template.id);

              return (
                <Box key={template.id}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {template.title}
                            </Typography>
                            {template.is_required && (
                              <Chip label="จำเป็น" size="small" color="error" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {template.description}
                          </Typography>
                        }
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setDialogOpen(true);
                        }}
                      >
                        ดูรายละเอียด
                      </Button>
                      
                      {hasUserConsent ? (
                        <>
                          <Chip
                            icon={<CheckCircle />}
                            label="ให้ความยินยอมแล้ว"
                            color="success"
                            size="small"
                          />
                          {!template.is_required && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => consentId && handleWithdrawConsent(consentId)}
                            >
                              ถอนความยินยอม
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckCircle />}
                          onClick={() => handleGrantConsent(template.id)}
                        >
                          ให้ความยินยอม
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                  {index < templates.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        )}

      {/* Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedTemplate?.title}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedTemplate?.description}
          </Typography>
          
          {selectedTemplate?.content_html ? (
            <Box
              dangerouslySetInnerHTML={{ __html: selectedTemplate.content_html }}
              sx={{ '& p': { mb: 2 }, '& ul, & ol': { pl: 3 } }}
            />
          ) : (
            <Typography variant="body2">ไม่มีรายละเอียดเพิ่มเติม</Typography>
          )}

          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              เวอร์ชัน: {selectedTemplate?.version}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ปิด</Button>
          {selectedTemplate && !hasConsent(selectedTemplate.id) && (
            <Button
              variant="contained"
              onClick={() => handleGrantConsent(selectedTemplate.id)}
            >
              ให้ความยินยอม
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ConsentManagementPage;
